import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { cn } from "@/lib/cn";

import { type PeriodOption } from "@/constants/periods";

// Rotation angles for fan effect (in degrees)
const ROTATION_ANGLES = [0, 1.243, 1.562, 2.485, 3.2, 4.0];

export interface PeriodPickerProps {
  options: PeriodOption[];
  value: string;
  onChange: (value: string) => void;
  size?: "sm" | "md";
  className?: string;
  buttonClassName?: string;
}

export const PeriodPicker = ({
  options,
  value,
  onChange,
  size = "md",
  className,
  buttonClassName,
}: PeriodPickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [portalContainer, setPortalContainer] = useState<HTMLElement>(() => document.body);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((o) => o.value === value) ?? options[0];
  const otherOptions = options.filter((o) => o.value !== value);

  // Update dropdown position when opened.
  // Список рендерится с position: fixed, поэтому координаты берутся ОТ ВЬЮПОРТА —
  // getBoundingClientRect() их уже такими и отдаёт. Прибавлять window.scrollY/scrollX
  // нельзя: на прокрученной странице список уезжает вниз ровно на величину прокрутки.
  useEffect(() => {
    if (!isOpen || !buttonRef.current) return;

    const updatePosition = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;

      setDropdownPos({
        top: rect.bottom + (size === "md" ? 2 : 1),
        left: rect.left,
      });
    };

    updatePosition();

    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen, size]);

  const handleToggle = useCallback(() => {
    setPortalContainer(
      containerRef.current?.closest<HTMLElement>('[role="dialog"]') ?? document.body
    );
    setIsOpen((prev) => !prev);
  }, []);

  const handleSelect = useCallback(
    (optionValue: string) => {
      onChange(optionValue);
      setIsOpen(false);
    },
    [onChange]
  );

  const handleBlur = useCallback((e: React.FocusEvent) => {
    // Close if focus leaves both the container and the dropdown (which is in a portal)
    const relatedTarget = e.relatedTarget as Node;
    if (
      !containerRef.current?.contains(relatedTarget) &&
      !dropdownRef.current?.contains(relatedTarget)
    ) {
      setIsOpen(false);
    }
  }, []);

  return (
    <div
      ref={containerRef}
      className={cn("relative inline-flex", className)}
      onBlur={handleBlur}
    >
      {/* Selected option (always visible) */}
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        className={cn(
          // Base styles
          "glass-blur relative z-10 flex items-center justify-center rounded-full whitespace-nowrap",
          "cursor-pointer font-semibold transition-all duration-200",
          // Size variants
          size === "sm" && "fs-300 px-3",
          size === "md" && "fs-300 px-4 py-2",
          // Neutral state
          "bg-neutral-50/5 font-semibold text-neutral-200 hover:bg-neutral-50/12",
          buttonClassName
        )}
      >
        {selectedOption.label}
      </button>

      {/* Other options - rendered via Portal to escape overflow:hidden */}
      {createPortal(
        <div
          ref={dropdownRef}
          style={{ top: dropdownPos.top, left: dropdownPos.left }}
          onBlur={handleBlur}
          className={cn("fixed z-50 flex flex-col items-start gap-px", size === "md" && "gap-0.5")}
        >
          {otherOptions.map((option, index) => {
            const rotationAngle =
              ROTATION_ANGLES[index + 1] ?? ROTATION_ANGLES[ROTATION_ANGLES.length - 1];

            return (
              <button
                key={option.value}
                type="button"
                onClick={() => handleSelect(option.value)}
                style={{
                  // Fan rotation only for md size
                  transform: isOpen && size === "md" ? `rotate(${rotationAngle}deg)` : undefined,
                  transitionDelay: isOpen ? `${index * 30}ms` : "0ms",
                }}
                className={cn(
                  // Base styles
                  "glass-blur flex items-center justify-center rounded-full whitespace-nowrap",
                  "cursor-pointer transition-all duration-300 ease-out",
                  // Size variants
                  size === "sm" && "fs-300 px-3",
                  size === "md" && "fs-300 origin-left px-4 py-2",
                  // Neutral state
                  "bg-neutral-50/5 font-semibold text-neutral-200",
                  "hover:bg-neutral-50/12",
                  // Visibility & position
                  isOpen ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0",
                  buttonClassName
                )}
              >
                {option.label}
              </button>
            );
          })}
        </div>,
        portalContainer
      )}
    </div>
  );
};
